import { useQuery, keepPreviousData, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch, ApiError } from "../../../lib/api.ts";
import { useTimeRange } from "../../../state/time-range.tsx";

/**
 * Data access for the hosted-service admin consoles (IDP / AIS / VCI), migrated into the control
 * plane. Each service exposes a JSON admin API; the BFF proxies it under
 * `/api/v1/service-admin/{slug}/<path>` (session-gated). This hook is the thin read client the
 * console screens use — it reuses `apiFetch` (same-origin, cookie auth, honest ApiError) and the
 * shared time-range so pausing freezes the console the same way it freezes every other screen.
 */

/** The three hosted services whose admin consoles the control plane hosts. */
export type ServiceAdminSlug = "idp" | "ais" | "vci";

/**
 * The BFF's 403 code for a service-admin control reserved to superadmins (src/worker/security.ts
 * `superadminRequired`): the AIS request log and the AIS credential-format write span every tenant.
 */
export const SUPERADMIN_REQUIRED_CODE = "superadmin_required";

/** True when a service-admin call was refused because the control is superadmin only. */
export function isSuperadminRequired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.code === SUPERADMIN_REQUIRED_CODE;
}

/** Build the proxy path for a service's admin-api sub-path. */
function adminPath(slug: ServiceAdminSlug, subpath: string): string {
  const clean = subpath.replace(/^\/+/, "");
  return `/service-admin/${slug}/${clean}`;
}

/**
 * Read a service admin-api endpoint as JSON. `key` namespaces the query cache; `enabled` lets a
 * screen defer the read until its tab is active. Live mode polls on the shared cadence and keeps the
 * previous data across refetches so the console updates in place (no reload flash).
 */
export function useServiceAdmin<T>(
  slug: ServiceAdminSlug,
  subpath: string,
  key: readonly unknown[],
  options: { enabled?: boolean } = {},
): UseQueryResult<T, ApiError> {
  const { live } = useTimeRange();
  return useQuery<T, ApiError>({
    queryKey: ["service-admin", slug, ...key],
    queryFn: ({ signal }) => apiFetch<T>(adminPath(slug, subpath), { signal }),
    enabled: options.enabled ?? true,
    staleTime: live ? 10_000 : 30_000,
    refetchInterval: live ? 15_000 : false,
    placeholderData: keepPreviousData,
    retry: 1,
  });
}

/** POST to a service admin-api endpoint (credential lifecycle actions). Returns the parsed body. */
export async function serviceAdminAction<T>(
  slug: ServiceAdminSlug,
  subpath: string,
  body?: unknown,
): Promise<T> {
  return apiFetch<T>(adminPath(slug, subpath), {
    method: "POST",
    ...(body !== undefined ? { body } : {}),
  });
}
